import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { PWAProvider } from './components/PWAProvider';
// import { SimpleMatomoProvider } from './contexts/SimpleMatomoContext';
// import './services/matomo'; // Initialize Matomo
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import PropertyReportPage from './pages/PropertyReportPage';
import PropertySearchPage from './pages/PropertySearchPage';
import LandSearchFlowPage from './pages/LandSearchFlowPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import SubscriptionCancelPage from './pages/SubscriptionCancelPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboard from './pages/AdminDashboard';
import LandReporterDashboard from './pages/LandReporterDashboard';
import DebugPropertyReportPage from './pages/DebugPropertyReportPage';
import SimpleLoginPage from './pages/SimpleLoginPage';
import AuthTestPage from './pages/AuthTestPage';
import LandSeekerPreviewPage from './pages/LandSeekerPreviewPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import PropertyVerificationPage from './pages/PropertyVerificationPage';
import AreaPreferencesPage from './pages/AreaPreferencesPage';
import RoleSelectionPage from './pages/RoleSelectionPage';
import AuthDebugPage from './pages/AuthDebugPage';
import VersionFooter from './components/VersionFooter';
import LandReporterOnboardingPage from './pages/LandReporterOnboardingPage';
import NotFoundPage from './pages/NotFoundPage';
import MaintenancePage from './pages/MaintenancePage';
import MaintenanceGate from './components/MaintenanceGate';
import './App.css';

const queryClient = new QueryClient();

function App() {
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const w = window;
      const existing = document.querySelector('script[data-mtm]');
      if (existing) return;
      w._mtm = w._mtm || [];
      w._mtm.push({ 'mtm.startTime': new Date().getTime(), event: 'mtm.Start' });
      const d = document; const g = d.createElement('script'); const s = d.getElementsByTagName('script')[0];
      g.async = true; g.src = 'https://analytics.ventaplaza.com/js/container_OOnvjeRo.js'; g.setAttribute('data-mtm', '1'); s.parentNode.insertBefore(g, s);
    }
  }, []);

  // Capture referral code from URL (?ref=XXXX) for land seekers and persist for signup.
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get('ref') || '').trim();
      if (ref) {
        // Basic sanity: short alphanumeric code
        if (/^[A-Za-z0-9_-]{4,32}$/.test(ref)) {
          localStorage.setItem('referral_code', ref);
        }
      }
    } catch (_e) {
      // ignore
    }
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <AuthProvider>
          <LanguageProvider>
            <PWAProvider>
              <Router future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true
              }}>
                <div className="App">
                  <main className="flex-1">
                    <MaintenanceGate>
                      <Routes>
                        {/* Some servers expose the SPA entry as /index.html; normalize to / */}
                        <Route path="/index.html" element={<Navigate to="/" replace />} />
                        <Route path="/maintenance" element={<MaintenancePage />} />
                        <Route path="/" element={<HomePage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/role-selection" element={<RoleSelectionPage />} />
                        <Route path="/simple-login" element={<SimpleLoginPage />} />
                        <Route path="/auth-test" element={<AuthTestPage />} />
                        <Route path="/report" element={<PropertyReportPage />} />
                        <Route path="/debug-report" element={<DebugPropertyReportPage />} />
                        <Route path="/search" element={<PropertySearchPage />} />
                        <Route path="/land-search" element={<LandSearchFlowPage />} />
                        <Route path="/preview" element={<LandSeekerPreviewPage />} />
                        <Route path="/property/:id" element={<PropertyDetailPage />} />
                        <Route path="/verify/:token" element={<PropertyVerificationPage />} />
                        <Route path="/preferences" element={<AreaPreferencesPage />} />
                        <Route path="/subscription" element={<SubscriptionPage />} />
                        <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
                        <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />
                        <Route path="/admin/login" element={<AdminLoginPage />} />
                        <Route path="/admin/dashboard" element={<AdminDashboard />} />
                        <Route path="/admin/properties/:id" element={<AdminDashboard />} />
                        <Route path="/reporter/dashboard" element={<LandReporterDashboard />} />
                        <Route path="/auth-debug" element={<AuthDebugPage />} />
                        <Route path="/onboarding/reporter" element={<LandReporterOnboardingPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </MaintenanceGate>
                  </main>
                  <VersionFooter />
                  <Toaster position="top-center" />
                </div>
              </Router>
            </PWAProvider>
          </LanguageProvider>
        </AuthProvider>
      </I18nextProvider>
    </QueryClientProvider>
  );
}

export default App;